import type { IDisposable, Terminal } from '@xterm/xterm';
import { cleanCopy, type CleanCopyContext } from './clean-copy';

/**
 * OSC 52 clipboard writes for a PTY terminal (FRONT-3003 review F13).
 *
 * A program in the terminal copies text by printing `ESC ] 52 ; <selection> ;
 * <base64 text> BEL`. xterm.js ignores that unless a handler is registered.
 * Claude Code's sign-in screen offers "(c to copy)" for its ~420-character
 * login URL and copies it exactly this way — the URL is hard-wrapped over
 * several rows, so selecting it with the mouse copies line breaks into it.
 *
 * Writes only: a read request (`?`) is ignored, so nothing in the terminal
 * can read the person's clipboard. Oversized, malformed or non-UTF-8
 * payloads are ignored. A clipboard the browser refuses (Safari, for one, wants
 * the write inside the key press's own gesture, and this one arrives a network
 * round trip later) is reported through `onRefused` so the person is told,
 * rather than pasting whatever was on the clipboard before (FRONT-3172).
 * Opt-in per terminal (`usePtyTerminal({ clipboard: true })`).
 *
 * A write is honoured only within `OSC52_INPUT_WINDOW_MS` of the person's last
 * KEY PRESS or MOUSE ACTION in that terminal, so output alone (say, `cat` of a
 * hostile file) cannot overwrite the clipboard. `onKey` is used, not `onData`:
 * onData also carries xterm's own replies to queries that program output can
 * provoke. A mouse action is a trusted `mousedown` on the terminal and the
 * `mouseup` that ends such a press (also when released outside); moves, wheel
 * and synthetic events never count. The mouse half exists because Claude Code's
 * full-screen renderer turns mouse tracking on, draws its own selection and
 * copies it with OSC 52 on mouse RELEASE — no key press precedes that copy.
 *
 * The text accepted after a MOUSE action is remembered as the "program
 * selection" so Cmd+C / the context menu's Copy can put it back on the clipboard
 * (xterm has no selection of its own in that mode). It is forgotten on a key
 * press and on a new left-button press (not on a right press, nor a macOS
 * Ctrl+left-click, which opens the menu). It is written UNCHANGED: Claude Code's
 * own copy text is already clean, unlike xterm's raw selection (`clean-copy.ts`).
 *
 * While the program owns the mouse (`modes.mouseTrackingMode` is not `none`) the
 * program selection wins over an xterm selection in BOTH the `copy` handler and
 * the context menu: xterm's right-click word-select runs even then and would
 * otherwise make Copy put ONE WORD on the clipboard. With no mouse tracking
 * (the classic window) xterm's selection wins and is cleaned. A `beforecopy`
 * listener cancels the event exactly when `copy` would put text on the
 * clipboard (both ask the same `copyText()`, so a whitespace-only selection
 * neither cancels nor copies), because WebKit fires `copy` only for a real
 * selection range or a cancelled `beforecopy`. Only the left and middle buttons
 * arm the document `mouseup` listener, a press whose release never arrives is
 * disarmed on window `blur` and on `contextmenu` (the native menu on macOS
 * swallows the release and fires no blur), a macOS Ctrl+left-click counts as the
 * right-click it is, and a release counts only within `OSC52_MAX_PRESS_MS` of
 * its press.
 */

/** How long after a key press or mouse action an OSC 52 write is still honoured. */
export const OSC52_INPUT_WINDOW_MS = 2_000;

/** Largest base64 payload accepted (~75 KB of text) — far above a URL, far below anything abusive. */
export const OSC52_MAX_PAYLOAD_CHARS = 100_000;

const SELECTION = /^[cpqs0-7]*$/;
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

/**
 * The text an OSC 52 sequence asks to copy, or null when it is not a write
 * this terminal honours. `data` is what xterm hands an OSC 52 handler: the
 * part after `52;`, i.e. `<selection>;<base64>` (selection may be empty).
 */
export function decodeOsc52Write(data: string): string | null {
  const sep = data.indexOf(';');
  if (sep === -1) return null;
  if (!SELECTION.test(data.slice(0, sep))) return null;
  const payload = data.slice(sep + 1);
  // `?` is a clipboard READ request (also not base64, so the shape check below would refuse it too).
  if (payload === '?' || payload.length === 0 || payload.length > OSC52_MAX_PAYLOAD_CHARS) return null;
  if (!BASE64.test(payload)) return null;
  try {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** Why an OSC 52 write may be honoured right now: the person's last key press or mouse action, whichever is newer and inside the window. */
export type Osc52Trigger = 'key' | 'mouse';

/** Pure decision behind the gate; a tie goes to the mouse. Null when neither action is recent enough. */
export function osc52Trigger(lastKeyAt: number, lastMouseAt: number, now: number): Osc52Trigger | null {
  const keyOk = now - lastKeyAt <= OSC52_INPUT_WINDOW_MS;
  const mouseOk = now - lastMouseAt <= OSC52_INPUT_WINDOW_MS;
  if (keyOk && mouseOk) return lastMouseAt >= lastKeyAt ? 'mouse' : 'key';
  if (mouseOk) return 'mouse';
  if (keyOk) return 'key';
  return null;
}

/** A mouse press whose release arrives later than this is not counted as a mouse action (the release never came, or the person walked away mid-press). */
export const OSC52_MAX_PRESS_MS = 60_000;

/** The slice of a DOM EventTarget this module uses, so the suite (no DOM) can inject fakes. */
export type EventTargetLike = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

/**
 * Where the mouse and copy listeners attach: the terminal's container, the
 * document that owns it and (optionally) its window, whose `blur` disarms a
 * press whose release will never arrive.
 */
export interface Osc52Targets {
  container: EventTargetLike;
  ownerDocument: EventTargetLike;
  ownerWindow?: EventTargetLike | null;
  /** Whether this is macOS (a Ctrl+left-click opens the context menu there). Injectable for tests; defaults to the browser's platform. */
  isMac?: boolean;
}

const detectMac = () => typeof navigator !== 'undefined' && /mac/i.test(navigator.platform ?? '');

interface MouseEventLike {
  isTrusted: boolean;
  button: number;
  ctrlKey?: boolean;
}

interface CopyEventLike {
  clipboardData: { setData(type: string, data: string): void } | null;
  preventDefault(): void;
  stopPropagation(): void;
}

/** Optional behaviour of `installOsc52Clipboard`, by name. */
export interface Osc52Options {
  /** The browser refused an OSC 52 write the terminal honoured (FRONT-3172). */
  onRefused?: () => void;
}

type CopyTerm = Pick<Terminal, 'getSelection' | 'getSelectionPosition' | 'cols' | 'textarea' | 'modes' | 'buffer'>;

/**
 * Register the OSC 52 write handler on `term` when `enabled`; returns one
 * disposable covering the handler and every listener, or null (and registers
 * nothing — no listener on the container or the document either) when not
 * enabled. `now` is injectable for tests. Without `targets` only the key gate
 * is installed; with them the mouse gate, the program selection and the copy /
 * context-menu handlers are too.
 *
 * `clean` turns xterm's raw selection into what the person meant to copy
 * (`cleanCopy`); it is applied to xterm's selection ONLY, never to OSC 52 text.
 *
 * `options.onRefused` runs when the browser refuses an honoured write
 * (FRONT-3172). It is named, not positional, so it can never bind to `clean`.
 */
export function installOsc52Clipboard(
  term: Pick<Terminal, 'parser' | 'onKey'> & Partial<CopyTerm>,
  enabled: boolean,
  writeText: (text: string) => Promise<void> | void,
  now: () => number = Date.now,
  targets?: Osc52Targets,
  clean: (raw: string, ctx: CleanCopyContext) => string = cleanCopy,
  options: Osc52Options = {},
): IDisposable | null {
  if (!enabled) return null;
  let lastKeyAt = -Infinity;
  let lastMouseAt = -Infinity;
  let programSelection: string | null = null;
  const keyListener = term.onKey(() => {
    lastKeyAt = now();
    programSelection = null;
  });
  const handler = term.parser.registerOscHandler(52, (data) => {
    const text = decodeOsc52Write(data);
    const trigger = text === null ? null : osc52Trigger(lastKeyAt, lastMouseAt, now());
    if (text !== null && trigger !== null) {
      if (trigger === 'mouse') programSelection = text;
      try {
        void Promise.resolve(writeText(text)).catch(() => options.onRefused?.());
      } catch {
        // A browser without clipboard access throws synchronously.
        options.onRefused?.();
      }
    }
    return true;
  });
  const disposeDom = targets ? installDomListeners(term, targets, {
    mouseAction: () => {
      lastMouseAt = now();
    },
    leftPress: () => {
      programSelection = null;
    },
    programSelection: () => programSelection,
    now,
    clean,
  }) : null;
  return {
    dispose: () => {
      handler.dispose();
      keyListener.dispose();
      disposeDom?.();
    },
  };
}

interface DomHooks {
  mouseAction(): void;
  leftPress(): void;
  programSelection(): string | null;
  now(): number;
  clean(raw: string, ctx: CleanCopyContext): string;
}

/**
 * Tracks the person's mouse presses in the terminal for the OSC 52 gate: a trusted press, and the
 * release of a press that started inside (listened for on the document, so a drag released outside
 * still counts). Returns `disarm` (stop waiting for a release) and the disposer.
 */
function trackPresses({ container, ownerDocument, ownerWindow, isMac = detectMac() }: Osc52Targets, hooks: DomHooks): { disarm: () => void; dispose: () => void } {
  let pressedAt = -Infinity;
  const disarm = () => ownerDocument.removeEventListener('mouseup', onMouseUp, true);
  function onMouseUp(event: Event) {
    const e = event as unknown as MouseEventLike;
    disarm();
    if (e.isTrusted && hooks.now() - pressedAt <= OSC52_MAX_PRESS_MS) hooks.mouseAction();
  }
  const onMouseDown = (event: Event) => {
    const e = event as unknown as MouseEventLike;
    if (!e.isTrusted) return;
    pressedAt = hooks.now();
    hooks.mouseAction();
    // A right press (or macOS Ctrl+click) opens the native context menu, which swallows the release: nothing to wait for, and it is no new selection either.
    const isRightPress = e.button === 2;
    const isMacCtrlClick = isMac && e.button === 0 && e.ctrlKey;
    if (isRightPress || isMacCtrlClick) return;
    if (e.button === 0) hooks.leftPress();
    // Capture on the document so a drag released outside the terminal still ends the press.
    disarm();
    if (e.button === 0 || e.button === 1) ownerDocument.addEventListener('mouseup', onMouseUp, true);
  };
  // A window that loses focus mid-press never delivers the release; without this the next click anywhere would open the copy window.
  const onBlur = () => disarm();
  container.addEventListener('mousedown', onMouseDown, true);
  ownerWindow?.addEventListener('blur', onBlur, false);
  return {
    disarm,
    dispose: () => {
      container.removeEventListener('mousedown', onMouseDown, true);
      ownerWindow?.removeEventListener('blur', onBlur, false);
      disarm();
    },
  };
}

/** What a copy right now would put on the clipboard, and the two facts the context menu needs. */
function copyDecision(term: Partial<CopyTerm>, hooks: DomHooks) {
  const xtermSelection = () => term.getSelection?.() ?? '';
  // While the program owns the mouse, xterm's own selection is only what its right-click word-select grabbed.
  const programOwnsMouse = () => (term.modes?.mouseTrackingMode ?? 'none') !== 'none';
  /** Where xterm's selection starts, plus the whole screen row it starts on (xterm's own buffer line) so the clean-up can judge a drag that starts after a list marker. */
  const selectionContext = (): CleanCopyContext => {
    const start = term.getSelectionPosition?.()?.start;
    return {
      startCol: start?.x ?? 0,
      cols: term.cols ?? 0,
      firstRow: start ? term.buffer?.active.getLine(start.y)?.translateToString(true) : undefined,
    };
  };
  /** The text a copy would put on the clipboard, or null (leave the event alone). Shared by `copy` and `beforecopy` so they cannot disagree. */
  const copyText = (): string | null => {
    const program = programOwnsMouse() ? hooks.programSelection() : null;
    if (program) return program;
    const selected = xtermSelection();
    const text = selected ? hooks.clean(selected, selectionContext()) : hooks.programSelection();
    return text || null;
  };
  return { copyText, xtermSelection, programOwnsMouse };
}

function installDomListeners(term: Partial<CopyTerm>, targets: Osc52Targets, hooks: DomHooks): () => void {
  const { container } = targets;
  const presses = trackPresses(targets, hooks);
  const { copyText, xtermSelection, programOwnsMouse } = copyDecision(term, hooks);
  // Capture phase: runs before xterm's own `copy` listener on `.xterm`, so it can replace what xterm would copy.
  const onCopy = (event: Event) => {
    const e = event as unknown as CopyEventLike;
    const text = copyText();
    if (!text || !e.clipboardData) return;
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
    e.stopPropagation();
  };
  // WebKit fires `copy` (and enables the menu's Copy) only for a real selection range or when `beforecopy` is cancelled; xterm's textarea holds just a caret in full-screen. The decision is `copy`'s own (`copyText() !== null`), never a second expression of it.
  const onBeforeCopy = (event: Event) => {
    if (copyText() !== null) (event as unknown as CopyEventLike).preventDefault();
  };
  // Bubble phase: runs after xterm's handler, which fills its hidden textarea only for ITS selection.
  const onContextMenu = () => {
    // The native menu swallows the press's mouseup (and fires no blur on macOS): stop waiting for it.
    presses.disarm();
    const text = hooks.programSelection();
    if (!text || !term.textarea) return;
    if (xtermSelection() && !programOwnsMouse()) return;
    term.textarea.value = text;
    term.textarea.select();
  };
  container.addEventListener('copy', onCopy, true);
  container.addEventListener('beforecopy', onBeforeCopy, true);
  container.addEventListener('contextmenu', onContextMenu, false);
  return () => {
    container.removeEventListener('copy', onCopy, true);
    container.removeEventListener('beforecopy', onBeforeCopy, true);
    container.removeEventListener('contextmenu', onContextMenu, false);
    presses.dispose();
  };
}
